import * as Yup from 'yup';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useAppDispatcher } from 'src/redux/store';
import { markAdvanceReceived } from 'src/redux/slices/cheque-factoring';

import FormProvider from 'src/components/hook-form';

import { IFactoringBatch } from 'src/types/cheque-factoring';

interface IMarkAdvanceReceivedDialog {
  batch: IFactoringBatch;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function MarkAdvanceReceivedDialog({
  batch,
  isOpen,
  onClose,
  onSuccess,
}: IMarkAdvanceReceivedDialog) {
  const dispatch = useAppDispatcher();

  const schema = useMemo(
    () =>
      Yup.object().shape({
        advanceReceivedDate: Yup.date().required('Advance received date is required'),
      }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { advanceReceivedDate: new Date() },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await dispatch(
        markAdvanceReceived({
          batchId: batch._id,
          advanceReceivedDate: new Date(data.advanceReceivedDate),
        })
      ).unwrap();

      enqueueSnackbar('Advance marked as received');
      reset();
      onSuccess();
      onClose();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, { variant: 'error' });
      } else {
        enqueueSnackbar(e?.message || 'Failed to mark advance as received', {
          variant: 'error',
        });
      }
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Mark Advance Received</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Confirm the date LOLC credited the 80% advance for this batch.
          </Typography>

          <Stack spacing={2}>
            <Controller
              name="advanceReceivedDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Advance Received Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Confirm
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default MarkAdvanceReceivedDialog;
